import { Warning, ShieldCheck, Wrench, Prohibit, Clock, Archive } from "@phosphor-icons/react";

const MAP = {
  available: { cls: "bg-emerald-100 text-emerald-900 border-emerald-500", Icon: ShieldCheck },
  restricted_use: { cls: "bg-amber-400 text-slate-900 border-amber-600", Icon: Warning },
  out_of_service: { cls: "bg-red-600 text-white border-red-800", Icon: Prohibit },
  under_maintenance: { cls: "bg-blue-100 text-blue-900 border-blue-500", Icon: Wrench },
  awaiting_inspection: { cls: "bg-amber-100 text-amber-900 border-amber-500", Icon: Clock },
  archived: { cls: "bg-slate-200 text-slate-500 border-slate-300", Icon: Archive },
};

export function StatusBadge({ status, className = "" }) {
  if (!status) return null;
  const m = MAP[status.code] || MAP.archived;
  const { Icon } = m;
  return (
    <span
      data-testid={`status-badge-${status.code}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 border-2 text-xs font-bold uppercase tracking-wide rounded ${m.cls} ${className}`}
    >
      <Icon size={14} weight="bold" />
      {status.name}
    </span>
  );
}

export function EffectiveBadge({ eff, className = "" }) {
  if (!eff) return null;
  const CLS = {
    green: "bg-emerald-100 text-emerald-900 border-emerald-500",
    amber: "bg-amber-400 text-slate-900 border-amber-600",
    red: "bg-red-600 text-white border-red-800",
  };
  const cls = CLS[eff.dot] || CLS.amber;
  const Icon = eff.dot === "red" ? Prohibit : eff.dot === "amber" ? Warning : ShieldCheck;
  return (
    <span
      data-testid={`eff-status-${eff.code}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 border-2 text-xs font-bold uppercase tracking-wide rounded ${cls} ${className}`}
    >
      <Icon size={14} weight="bold" />
      {eff.name}
    </span>
  );
}

export function UseStatusDecision({ decision, testid = "use-status-decision" }) {
  if (!decision) return null;
  const dot = decision.dot || "green";
  const PANEL = {
    green: "bg-emerald-600 border-emerald-800 text-white",
    amber: "bg-amber-400 border-amber-600 text-slate-900",
    red: "bg-red-600 border-red-800 text-white",
  };
  const Icon = dot === "red" ? Prohibit : dot === "amber" ? Warning : ShieldCheck;
  return (
    <div data-testid={testid} className={`w-full border-y-4 ${PANEL[dot] || PANEL.amber} px-4 py-5 flex flex-col items-center text-center`}>
      <div className="font-display text-[11px] font-black uppercase tracking-[0.3em] opacity-90">Can I use this asset?</div>
      <div className="flex items-center justify-center gap-3 mt-1">
        <Icon size={dot === "green" ? 30 : 40} weight="fill" />
        <span data-testid={`use-status-headline-${dot}`} className={`font-display ${dot === "green" ? "text-2xl" : "text-3xl sm:text-4xl"} font-black uppercase tracking-wide`}>{decision.headline}</span>
      </div>
      {decision.reasons?.length > 0 && (
        <div className="mt-3 text-left inline-block max-w-lg">
          <div className="font-display text-xs font-black uppercase tracking-widest">{dot === "red" ? "Reasons" : "Restriction"}</div>
          <ul className="mt-1 font-body text-sm font-semibold space-y-0.5">
            {decision.reasons.map((r, i) => <li key={i} data-testid={`use-status-reason-${i}`}>• {r}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export function LatestCheckLine({ lc, testid = "latest-check" }) {
  const defects = lc && ((lc.defect_count || 0) > 0 || lc.outcome === "fail");
  const when = lc?.completed_at ? new Date(lc.completed_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div data-testid={testid} className="text-center">
      <span className="font-display text-[11px] font-black uppercase tracking-widest text-slate-400 block">Latest Check</span>
      {!lc ? (
        <div className="font-body text-sm text-slate-500">○ No pre-use check on record</div>
      ) : (
        <>
          <div className={`font-body text-base font-bold ${defects ? "text-amber-700" : "text-emerald-700"}`}>
            {defects ? `⚠ ${lc.defect_count > 0 ? `${lc.defect_count} defect${lc.defect_count > 1 ? "s" : ""} reported` : "Defects reported"}` : "✓ Nil defects reported"}
          </div>
          <div className="font-mono text-xs text-slate-500">{lc.by_name ? `${lc.by_name} · ` : ""}{when}</div>
        </>
      )}
    </div>
  );
}

export function StatusBanner({ status, notices = [] }) {
  if (!status) return null;
  const oos = status.code === "out_of_service";
  const blocks = status.blocks_use;
  const m = MAP[status.code] || MAP.archived;
  const { Icon } = m;
  const critical = oos || (blocks && status.is_safety_critical);
  return (
    <div
      data-testid="asset-status-banner"
      className={`w-full border-y-4 ${m.cls} ${critical ? "py-6" : "py-4"} px-4 flex flex-col items-center text-center`}
    >
      <div className="flex items-center gap-3">
        <Icon size={critical ? 40 : 28} weight="fill" />
        <span className={`font-display ${critical ? "text-3xl sm:text-4xl" : "text-xl"} font-black uppercase tracking-widest`}>
          {status.name}
        </span>
      </div>
      {oos && <div className="mt-1 font-display text-xl font-black uppercase tracking-widest">DO NOT USE</div>}
      {notices.filter((n) => n.priority === "critical").map((n) => (
        <p key={n.id} className="mt-2 font-body text-sm font-semibold">{n.text}</p>
      ))}
    </div>
  );
}
